// stores/pageContent.js
import { defineStore } from "pinia";
import { useI18n } from "vue-i18n";
import { useShortcodeParser } from "~/composables/useShortcodeParser";

export const usePageContentStore = defineStore("pageContent", {
  state: () => ({
    title: "",
    description: "",
    content: "",
    customHead: null,
    customFooter: null,
    verification: null,
    breadcrumbs: [],
    error: null,
    loading: false, // Add loading state
    // Raw API response cached by fetchPageMeta, keyed by path, so
    // fetchPageContent (called later in onMounted) can reuse it instead of
    // hitting the same endpoint a second time. Cleared once consumed, and
    // excluded from persistence (see `persist.paths` below).
    _cachedPath: null,
    _cachedResponse: null,
  }),
  actions: {
    // SSR-safe metadata-only fetch: skips parseShortcodes (which uses
    // document/createApp and would crash during SSR/prerender), so pages can
    // await this at setup time to get a correct <title>/description in the
    // server-rendered HTML, before the full content fetch runs in onMounted.
    async fetchPageMeta(path) {
      const { baseApi } = useApiBase();
      const endpoint = `${baseApi}${path}`;

      const response = await $fetch(`${endpoint}`).catch(() => null);
      this._cachedPath = path;
      this._cachedResponse = response;

      if (!response?.data) return;

      this.title = response.data.alternate_title || response.data.title || "";
      this.description = response.data.excerpt || "";
      this.verification = response.data.verification || null;
      this.breadcrumbs = response.data.breadcrumbs || [];
    },
    async fetchPageContent(path) {
      this.startLoading();

      const { locale } = useI18n();
      const { parseShortcodes } = useShortcodeParser();

      try {

        // Reuse the response fetchPageMeta already fetched for this path
        // (during setup/SSR) instead of calling the same endpoint again.
        let response;
        if (this._cachedPath === path) {
          response = this._cachedResponse;
        } else {
          const { baseApi } = useApiBase();
          const endpoint = `${baseApi}${path}`;
          response = await $fetch(`${endpoint}`).catch(() => null);
        }
        this._cachedPath = null;
        this._cachedResponse = null;

        if (!response) {

          // throw new Error("Error loading page content");
          await navigateTo(`/${locale.value}/page-not-found`);
          return;
        }

        if (!response.data) {
          // No generic CMS content for this path — leave content blank
          // rather than treating it as an error.
          this.setPageData({ title: "", description: "", content: "", customHead: null, customFooter: null, verification: null, breadcrumbs: [] });
          return;
        }

        const rawContent = response.data.body;
        const parsedContent = await parseShortcodes(rawContent);

        const pageData = {
          title: response.data.alternate_title || response.data.title || "",
          description: response.data.excerpt || "",
          content: parsedContent || "",
          customHead: response.data.custom_head,
          customFooter: response.data.custom_footer,
          verification: response.data.verification || null,
          breadcrumbs: response.data.breadcrumbs || [],
        };
  
        // Set the page data and cache it in localStorage
        this.setPageData(pageData);

      } catch (err) {
        this.setError(err.message || "An error occurred while fetching the content.");
      }

      await new Promise((resolve) => setTimeout(resolve, 150));
    },
    setPageData({ title, description, content, customHead, customFooter, verification, breadcrumbs}) {
      this.title = title;
      this.description = description;
      this.content = content;
      this.customHead = customHead;
      this.customFooter = customFooter;
      this.verification = verification;
      this.breadcrumbs = breadcrumbs || [];
      this.error = null;
      this.loading = false; // Stop loading after setting data
    },
    setError(errorMessage) {
      this.error = errorMessage;
      this.title = "Error";
      this.description = "";
      this.content = "";
      this.customHead = "";
      this.customFooter = "";
      this.breadcrumbs = [];
      this.loading = false; // Stop loading on error
    },
    startLoading() {
      this.loading = true; // Start loading
    },
  },
  // Exclude the raw fetchPageMeta/fetchPageContent response cache — it's only
  // needed transiently within a single page load and would otherwise
  // duplicate the (potentially large) CMS body in localStorage.
  persist: {
    pick: ["title", "description", "content", "customHead", "customFooter", "verification", "breadcrumbs"],
  },
});